import { baseService } from "src/store/base.store";
import { User, UserStats, UserBalance, UserSpaceStats } from "../types/user.entity";
import { userRepository } from "../repositories/user.repository";
import { userSpaceStatsRepository, userStatsRepository } from "../repositories/user-stats.repository";
import { userBalanceRepository } from "../repositories/user-balance.repository";
import { userBlocksRepository } from "../repositories/user-blocks.repository";
import { userSpaceSwitchHistoryRepository } from "../repositories/user-space-switch-history.repository";
import { meetingPolicyRepository } from "../repositories/meeting-policy.repository";
import { executeQuery } from "src/store";
import { callFunctionSafe } from "src/firebase/functions";
import { FilterQuery } from "src/firebase/firestore/types";
import {
  collection,
  getDoc,
  getDocs,
  limit as fsLimit,
  orderBy,
  query,
  startAfter,
  where as fsWhere,
  doc,
  documentId,
  QueryConstraint,
} from "firebase/firestore";
import { db } from "src/firebase";
import { UserBlocks } from "../types/user-blocks.entity";
import { UserSpaceSwitchHistory } from "../types/user-space-switch-history.entity";
import { MeetingPolicyEvent, MeetingPolicyRole, UserMeetingPolicyStatsByRole } from "../types/meeting-policy.entity";

/** Payload for deleteLearningSpace callable. Matches backend validateDeleteLearningSpaceSchema: z.object({ userId: z.string().optional() }) */
export type DeleteLearningSpacePayload = { userId: string };

/** Payload for addNewLearningSpace callable (matches backend request payload content) */
export type AddNewLearningSpacePayload = {
  userId: string;
  langCode: string;
  langLevel?: "A1" | "A2" | "B1" | "B2" | "C1" | "C2";
};

/** Payload for skipLearnerTrackUntilChapter callable. */
export type SkipLearnerTrackUntilChapterPayload = {
  learnerTrackId: string;
  skipUntil: string;
};

/** Response for skipLearnerTrackUntilChapter callable. */
export type SkipLearnerTrackUntilChapterResponse = {
  skippedTopicsCount: number;
};

/** Payload for deleteLearnerTrackLevelProgress callable. */
export type DeleteLearnerTrackLevelProgressPayload = {
  learnerTrackId: string;
};

/** Response for deleteLearnerTrackLevelProgress callable. */
export type DeleteLearnerTrackLevelProgressResponse = {
  deletedTopicsCount: number;
};

export type ResetMeetingPolicyStatsPayload = {
  userId: string;
  role: MeetingPolicyRole;
  reason?: string;
  resetAll?: boolean;
};

export interface UsersResponse {
  users: User[];
  total: number;
}

export interface PaginationParams {
  page: number;
  limit: number;
}

export interface UsersFilter {
  userId?: string;
  name?: string;
  email?: string;
  "appProfile.phoneNumber"?: string;
  isSessionTemporarilyDisabled?: boolean;
  organizationId?: string;
  projectId?: string;
}

export const buildUserWhere = async (where: UsersFilter): Promise<FilterQuery<User>["where"]> => {
  const q: any = {}; // Using any to handle nested field notation

  // Note: userId cannot be queried directly in Firestore where clauses
  // It will be handled separately in the query functions

  if (where.name) {
    q["name"] = where.name;
  }

  if (where.email) {
    q["email"] = where.email;
  }

  if (where["appProfile.phoneNumber"]) {
    q["appProfile.phoneNumber"] = where["appProfile.phoneNumber"];
  }

  if (where.isSessionTemporarilyDisabled !== undefined) {
    q["appMeta.isSessionTemporarilyDisabled"] = where.isSessionTemporarilyDisabled;
  }

  if (where.organizationId) {
    q["organizationId"] = where.organizationId;
  }

  if (where.projectId) {
    q["projectId"] = where.projectId;
  }

  return q;
};

export const userService = baseService.injectEndpoints({
  endpoints: (builder) => ({
    getUsers: builder.query<User[], { where?: UsersFilter; cursor?: { docId: string }; limit?: number }>({
      keepUnusedDataFor: 0, // Disable caching for debugging
      queryFn: async ({ where: filters, cursor, limit }) => {
        try {
          // Handle userId search separately (direct document lookup)
          if (filters?.userId) {
            try {
              const user = await userRepository.findById(filters.userId);
              return { data: user ? [user] : [] };
            } catch (error) {
              console.error("Error finding user by ID:", error);
              return { data: [] };
            }
          }

          const filterQuery = await buildUserWhere(filters || {});
          const pageSize = typeof limit === "number" && limit > 0 ? limit : 10;

          const usersRef = collection(db, "users");
          const constraints: QueryConstraint[] = [
            ...Object.entries(filterQuery || {}).map(([field, value]) => fsWhere(field, "==", value)),
            fsWhere("createdAt", "!=", null),
            orderBy("createdAt", "desc"),
            orderBy(documentId(), "desc"),
            fsLimit(pageSize),
          ];

          if (cursor?.docId) {
            const cursorSnap = await getDoc(doc(usersRef, cursor.docId));
            if (cursorSnap.exists()) {
              constraints.push(startAfter(cursorSnap));
            }
          }

          const usersQuery = query(usersRef, ...constraints);
          const snapshot = await getDocs(usersQuery);
          const users = snapshot.docs.map((docSnap) => ({ ...(docSnap.data() as User), id: docSnap.id }));
          const toMillis = (value: unknown) => {
            if (!value) return 0;
            if (typeof value === "object" && value !== null && "seconds" in (value as any)) {
              return Number((value as any).seconds) * 1000;
            }
            if (value instanceof Date) return value.getTime();
            const parsed = Date.parse(String(value));
            return Number.isNaN(parsed) ? 0 : parsed;
          };
          users.sort((a, b) => {
            const diff = toMillis(b.createdAt) - toMillis(a.createdAt);
            if (diff !== 0) return diff;
            return b.id.localeCompare(a.id);
          });
          return { data: users };
        } catch (error) {
          return {
            error: {
              status: 500,
              data: error instanceof Error ? error.message : "Failed to fetch users",
            },
          };
        }
      },
      providesTags: ["users"],
    }),
    countUsers: builder.query<number, { where?: UsersFilter }>({
      queryFn: async ({ where }) => {
        try {
          // Handle userId search separately (direct document lookup)
          if (where?.userId) {
            try {
              const user = await userRepository.findById(where.userId);
              return { data: user ? 1 : 0 };
            } catch (error) {
              console.error("Error counting user by ID:", error);
              return { data: 0 };
            }
          }

          const baseFilter = await buildUserWhere(where || {});
          const filterQuery = {
            ...baseFilter,
            createdAt: { $exists: true },
          };
          const total = await userRepository.count(filterQuery);
          return { data: total };
        } catch (error) {
          return {
            error: {
              status: 500,
              data: error instanceof Error ? error.message : "Failed to count users",
            },
          };
        }
      },
      providesTags: ["users"],
    }),
    getUserById: builder.query<User, string>({
      queryFn: async (id) => {
        console.log("Fetching user by ID:", id);
        return executeQuery(() => userRepository.findById(id));
      },
      keepUnusedDataFor: 600,
    }),
    getUsersByIds: builder.query<Record<string, User>, string[]>({
      queryFn: async (userIds) => {
        try {
          if (!userIds || userIds.length === 0) {
            return { data: {} };
          }
          
          const users = await userRepository.findByIds(userIds);
          const usersMap: Record<string, User> = {};
          users.forEach((user) => {
            if (user) {
              usersMap[user.id] = user;
            }
          });
          
          return { data: usersMap };
        } catch (error) {
          return {
            error: {
              status: 500,
              data: error instanceof Error ? error.message : "Failed to fetch users",
            },
          };
        }
      },
      keepUnusedDataFor: 600,
    }),
    getUserBlocks: builder.query<UserBlocks, string>({
      queryFn: async (userId) => {
        try {
          const blocksDoc = await userBlocksRepository.findById(userId);
          const blockedUsersIds: string[] = Array.isArray(blocksDoc?.blockedUsersIds)
            ? [...blocksDoc!.blockedUsersIds]
            : [];
          const blockedByUsersIds: string[] = Array.isArray(blocksDoc?.blockedByUsersIds)
            ? [...blocksDoc!.blockedByUsersIds]
            : [];
          return {
            data: {
              blockedUsersIds,
              blockedByUsersIds,
            },
          };
        } catch (error) {
          return {
            error: {
              status: 500,
              data: error instanceof Error ? error.message : "Failed to fetch user blocks",
            },
          };
        }
      },
      keepUnusedDataFor: 300,
    }),
    getUserSpaceSwitchHistory: builder.query<UserSpaceSwitchHistory[], string>({
      queryFn: async (userId) => {
        try {
          const history = await userSpaceSwitchHistoryRepository.getByUserId(userId);
          return { data: history };
        } catch (error) {
          return {
            error: {
              status: 500,
              data: error instanceof Error ? error.message : "Failed to fetch user space switch history",
            },
          };
        }
      },
      keepUnusedDataFor: 300,
    }),
    getUserMeetingPolicyStats: builder.query<UserMeetingPolicyStatsByRole, string>({
      queryFn: async (userId) => {
        try {
          const stats = await meetingPolicyRepository.getStatsByUserId(userId);
          return { data: stats };
        } catch (error) {
          return {
            error: {
              status: 500,
              data: error instanceof Error ? error.message : "Failed to fetch user meeting policy stats",
            },
          };
        }
      },
      keepUnusedDataFor: 300,
    }),
    getUserMeetingPolicyEvents: builder.query<MeetingPolicyEvent[], string>({
      queryFn: async (userId) => {
        try {
          const events = await meetingPolicyRepository.getEventsByUserId(userId, 75);
          return { data: events };
        } catch (error) {
          return {
            error: {
              status: 500,
              data: error instanceof Error ? error.message : "Failed to fetch user meeting policy events",
            },
          };
        }
      },
      keepUnusedDataFor: 300,
    }),
    resetMeetingPolicyStats: builder.mutation<void, ResetMeetingPolicyStatsPayload>({
      queryFn: async (payload) => {
        try {
          const safePayload = JSON.parse(
            JSON.stringify({
              userId: payload.userId,
              role: payload.role,
              resetAll: payload.resetAll ?? false,
              ...(payload.reason?.trim() ? { reason: payload.reason.trim() } : {}),
            })
          ) as ResetMeetingPolicyStatsPayload;

          await callFunctionSafe<void, ResetMeetingPolicyStatsPayload>("resetMeetingPolicyStats", safePayload);
          return { data: undefined };
        } catch (error) {
          const message =
            typeof error === "object" && error !== null && "error" in error
              ? String((error as { error?: unknown }).error)
              : error instanceof Error
                ? error.message
                : "Failed to reset meeting policy stats";

          return {
            error: {
              status: 500,
              data: message,
            },
          };
        }
      },
      invalidatesTags: ["users"],
    }),
    deleteUserById: builder.mutation<void, string>({
      queryFn: async (id) => {
        const response = await callFunctionSafe<{ success: boolean }>("deleteUserProfileById", { userId: id });
        if ("data" in response && response.data.success) {
          return { data: undefined };
        } else {
          return {
            error: {
              status: 500,
              data: "Failed to delete user",
            },
          };
        }
      },
      invalidatesTags: ["users"],
    }),
    updateUserProfile: builder.mutation<
      void,
      {
        userId: string;
        field: "ENABLE_SESSIONS" | "BAN_USER" | "UNBAN_USER";
      }
    >({
      queryFn: async ({ userId, field }) => {
        const response = await callFunctionSafe<{
          success: boolean;
        }>("updateUserProfileByAdmin", {
          userId,
          field,
        });
        if ("data" in response && response.data.success) {
          if (field === "ENABLE_SESSIONS") {
            await userRepository.update(
              userId,
              {
                "appMeta.isSessionTemporarilyDisabled": false,
                "appMeta.isInActiveRoom": false,
              } as any
            );
          }
          return { data: undefined };
        } else {
          return {
            error: {
              status: 500,
              data: "Failed to update user profile",
            },
          };
        }
      },
      invalidatesTags: ["users"],
    }),
    addNewLearningSpace: builder.mutation<void, AddNewLearningSpacePayload>({
      queryFn: async (payload) => {
        try {
          const userIdStr =
            typeof payload.userId === "string"
              ? payload.userId
              : (payload.userId as any)?.id != null
                ? String((payload.userId as any).id)
                : String(payload.userId ?? "");
          // Send a strict plain object so backend Zod receives expected shape (no extra/undefined fields)
          const safePayload = JSON.parse(
            JSON.stringify({
              userId: userIdStr,
              langCode: payload.langCode,
              ...(payload.langLevel ? { langLevel: payload.langLevel } : {}),
            })
          ) as AddNewLearningSpacePayload;
          await callFunctionSafe<void>("addNewLearningSpace", safePayload);
          return { data: undefined };
        } catch (error) {
          const msg =
            (typeof error === "object" && error !== null && "error" in error && typeof (error as any).error === "string"
              ? String((error as any).error)
              : error instanceof Error
                ? error.message
                : "Failed to add learning space");
          return {
            error: {
              status: 500,
              data: msg,
            },
          };
        }
      },
      invalidatesTags: ["users"],
    }),
    deleteLearningSpace: builder.mutation<void, DeleteLearningSpacePayload>({
      queryFn: async (payload) => {
        try {
          const userIdStr =
            typeof payload.userId === "string"
              ? payload.userId
              : (payload.userId as any)?.id != null
                ? String((payload.userId as any).id)
                : String(payload.userId ?? "");
          // Send a strict plain object so backend Zod receives { userId: string } (no extra fields)
          const requestPayload = JSON.parse(
            JSON.stringify({ userId: userIdStr })
          ) as DeleteLearningSpacePayload;
          await callFunctionSafe<void>("deleteLearningSpace", requestPayload);
          return { data: undefined };
        } catch (error) {
          const msg =
            (typeof error === "object" && error !== null && "error" in error && typeof (error as any).error === "string"
              ? String((error as any).error)
              : error instanceof Error
                ? error.message
                : "Failed to delete learning space");
          return {
            error: {
              status: 500,
              data: msg,
            },
          };
        }
      },
      invalidatesTags: ["users"],
    }),
    skipLearnerTrackUntilChapter: builder.mutation<
      SkipLearnerTrackUntilChapterResponse,
      SkipLearnerTrackUntilChapterPayload
    >({
      queryFn: async (payload) => {
        try {
          const response = await callFunctionSafe<SkipLearnerTrackUntilChapterResponse>(
            "skipLearnerTrackUntilChapter",
            payload
          );

          if ("data" in response) {
            return { data: response.data };
          }

          return {
            error: {
              status: 500,
              data: "Failed to skip learner track until chapter",
            },
          };
        } catch (error) {
          return {
            error: {
              status: 500,
              data: error instanceof Error ? error.message : "Failed to skip learner track until chapter",
            },
          };
        }
      },
      invalidatesTags: ["users"],
    }),
    deleteLearnerTrackLevelProgress: builder.mutation<
      DeleteLearnerTrackLevelProgressResponse,
      DeleteLearnerTrackLevelProgressPayload
    >({
      queryFn: async (payload) => {
        try {
          const response = await callFunctionSafe<DeleteLearnerTrackLevelProgressResponse>(
            "deleteLearnerTrackLevelProgress",
            payload
          );

          if ("data" in response) {
            return { data: response.data };
          }

          return {
            error: {
              status: 500,
              data: "Failed to delete learner track level progress",
            },
          };
        } catch (error) {
          return {
            error: {
              status: 500,
              data: error instanceof Error ? error.message : "Failed to delete learner track level progress",
            },
          };
        }
      },
      invalidatesTags: ["users"],
    }),
    getUserStats: builder.query<UserStats | null, string>({
      queryFn: async (userId) => {
        try {
          const stats = await userStatsRepository.findByUserId(userId);
          return { data: stats };
        } catch (error) {
          return {
            error: {
              status: 500,
              data: error instanceof Error ? error.message : "Failed to fetch user stats",
            },
          };
        }
      },
      keepUnusedDataFor: 300, // Cache for 5 minutes
    }),
    getUserSpaceStats: builder.query<UserSpaceStats[], string>({
      queryFn: async (userId) => {
        try {
          const stats = await userSpaceStatsRepository.findByUserId(userId);
          return { data: stats };
        } catch (error) {
          return {
            error: {
              status: 500,
              data: error instanceof Error ? error.message : "Failed to fetch user space stats",
            },
          };
        }
      },
      keepUnusedDataFor: 300,
    }),
    getUserBalance: builder.query<UserBalance | null, string>({
      queryFn: async (userId) => {
        try {
          const balance = await userBalanceRepository.findByUserId(userId);
          return { data: balance };
        } catch (error) {
          return {
            error: {
              status: 500,
              data: error instanceof Error ? error.message : "Failed to fetch user balance",
            },
          };
        }
      },
      keepUnusedDataFor: 300, // Cache for 5 minutes
    }),
    setUserOfflineByAdmin: builder.mutation<{ success: boolean }, string>({
      queryFn: async (userId) => {
        try {
          const response = await callFunctionSafe<{ success: boolean }>("setUserOfflineByAdmin", {
            userId,
          });
          if ("data" in response && response.data.success) {
            return { data: response.data };
          } else {
            return {
              error: {
                status: 500,
                data: "Failed to set user offline",
              },
            };
          }
        } catch (error) {
          return {
            error: {
              status: 500,
              data: error instanceof Error ? error.message : "Failed to set user offline",
            },
          };
        }
      },
      invalidatesTags: ["users"],
    }),
  }),
});

export const {
  useGetUsersQuery,
  useCountUsersQuery,
  useGetUserByIdQuery,
  useGetUsersByIdsQuery,
  useGetUserBlocksQuery,
  useGetUserSpaceSwitchHistoryQuery,
  useGetUserMeetingPolicyStatsQuery,
  useGetUserMeetingPolicyEventsQuery,
  useResetMeetingPolicyStatsMutation,
  useDeleteUserByIdMutation,
  useUpdateUserProfileMutation,
  useAddNewLearningSpaceMutation,
  useDeleteLearningSpaceMutation,
  useSkipLearnerTrackUntilChapterMutation,
  useDeleteLearnerTrackLevelProgressMutation,
  useGetUserStatsQuery,
  useGetUserSpaceStatsQuery,
  useGetUserBalanceQuery,
  useSetUserOfflineByAdminMutation,
} = userService;
